import { useEffect, useRef } from 'react'
import { useInventoryStore } from '../store'
import { isBonus, type Bonus } from '../types/domain'
import { selectFreeBets } from '../utils/selectFreeBets'
import { pickClassicAutoApplyFreeBet } from '../utils/pickClassicAutoApplyFreeBet'

interface Options {
  gameId?: string
  /** Minimum bet in display units; free bets below it are skipped. */
  minBetAmount?: number
  /** Current panel stake (display units) — used to detect drift from the free-bet amount. */
  betAmount: string
  /** Syncs the panel stake to the free-bet amount (as `handleActivateBonus` does). */
  setBetAmount: (betAmount: string) => void
}

/**
 * Classic bet panel: auto-applies the oldest usable claimed free bet (FIFO, matching chip
 * placement) and LOCKS the panel stake to its amount. The stake is re-locked whenever it
 * drifts from the free-bet amount — e.g. after a cancel/round reset, or when another flow
 * (sidebar/banner) activated the bonus without syncing the stake — so a free bet can never
 * be placed at the panel default. One at a time: when the active free bet is consumed the
 * store clears `activeBonusId` and the next usable one applies.
 */
export const useAutoApplyClassicFreeBet = ({
  gameId,
  minBetAmount,
  betAmount,
  setBetAmount
}: Options) => {
  const items = useInventoryStore((state) => state.items)
  const activeBonusId = useInventoryStore((state) => state.activeBonusId)
  const activateItem = useInventoryStore((state) => state.actions.activateItem)

  const setBetAmountRef = useRef(setBetAmount)
  setBetAmountRef.current = setBetAmount

  useEffect(() => {
    // Use the already-active free bet if there is one; otherwise auto-apply the oldest usable.
    const activeItem = activeBonusId ? items.find((item) => item.id === activeBonusId) : undefined
    let active: Bonus | null = activeItem && isBonus(activeItem) ? activeItem : null

    if (!active) {
      const next = pickClassicAutoApplyFreeBet(
        selectFreeBets(items, { status: 'claimed', gameId }),
        minBetAmount
      )
      if (!next) return
      activateItem(next.id)
      active = next
    }

    // Lock the stake to the free-bet amount; re-applied on any drift so the panel never
    // places a free bet at the default stake.
    if (parseFloat(betAmount) !== active.amount) {
      setBetAmountRef.current(active.amount.toString())
    }
  }, [items, activeBonusId, gameId, minBetAmount, betAmount, activateItem])
}
